import {TextField} from '@mui/material';
import {isMac} from '@wandb/weave/common/util/browser';
import React, {useEffect, useRef} from 'react';

interface TextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onClose: (value?: any) => void;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  autoFocus?: boolean;
}

export const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  onClose,
  inputRef,
  autoFocus = true,
}) => {
  const localInputRef = useRef<HTMLTextAreaElement | null>(null);
  const actualInputRef = inputRef || localInputRef;

  // Focus input when autoFocus is true
  useEffect(() => {
    if (autoFocus) {
      // Small delay to ensure the component is fully mounted
      const timer = setTimeout(() => {
        if (actualInputRef.current) {
          actualInputRef.current.focus();
          // Position cursor at end instead of selecting all
          const len = actualInputRef.current.value.length;
          actualInputRef.current.setSelectionRange(len, len);
          // Scroll to bottom when focusing
          actualInputRef.current.scrollTop =
            actualInputRef.current.scrollHeight;
        }
      }, 10);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoFocus, actualInputRef]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const isCmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;

    if (event.key === 'Enter' && isCmdOrCtrl) {
      // Cmd/Ctrl+Enter: save and exit
      event.preventDefault();
      event.stopPropagation();
      onClose(value);
    } else if (event.key === 'Enter' && !isCmdOrCtrl) {
      // Enter: allow line break (default behavior)
      event.stopPropagation();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      // Escape should cancel without applying changes
      onClose();
    }
  };

  return (
    <TextField
      inputRef={actualInputRef}
      value={value}
      onChange={e => {
        const newValue = e.target.value;
        onChange(newValue);
      }}
      onKeyDown={handleKeyDown}
      // Focus the input and position cursor at the end
      onFocus={e => {
        const target = e.target as HTMLTextAreaElement;
        target.setSelectionRange(target.value.length, target.value.length);
        // Scroll to bottom when focusing
        target.scrollTop = target.scrollHeight;
      }}
      fullWidth
      multiline
      autoFocus={autoFocus}
      sx={{
        width: '100%',
        height: '100%',
        '& .MuiInputBase-root': {
          fontFamily: '"Source Sans Pro", sans-serif',
          fontSize: '14px',
          border: 'none',
          backgroundColor: 'white',
          height: '100%',
        },
        '& .MuiInputBase-input': {
          padding: '0px',
          height: '100%',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& textarea': {
          overflow: 'auto !important',
          height: '100% !important',
          resize: 'none',
        },
      }}
    />
  );
};
